import { useEffect, useMemo, useState } from "react";
import {
  ImageIcon,
  Wand2,
  Download,
  Dices,
  Loader2,
  AlertTriangle,
  Upload,
  X,
  Info,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import GenerationProgress from "../components/GenerationProgress.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";
import { generateImage, getModels } from "../lib/api.js";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { resolvePromptText } from "../lib/promptText.js";
import { useI18n } from "../lib/i18n.jsx";

const PRESETS = [
  "A neon cyberpunk skyline at dusk, rain slicked streets, cinematic, 35mm",
  "A cute robot reading a book in a cozy library, soft light, pixar style",
  "Macro photo of a dewdrop on a leaf at sunrise, bokeh, ultra detailed",
  "Isometric tiny floating island with a waterfall, vibrant colors",
];

const SIZES = [
  { label: "Square 1024", w: 1024, h: 1024 },
  { label: "Landscape 1344×768", w: 1344, h: 768 },
  { label: "Portrait 768×1344", w: 768, h: 1344 },
];

export default function ImageGen() {
  const { t } = useI18n();
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("black-forest-labs/flux.3-image-klein");
  const [prompt, setPrompt] = useState("");
  const [negative, setNegative] = useState("");
  const [size, setSize] = useState(SIZES[0]);
  const [seed, setSeed] = useState(0);
  const [refImage, setRefImage] = useState(null);
  const [strength, setStrength] = useState(0.7);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    getModels()
      .then((m) => setModels(m.image || []))
      .catch(() => {});
  }, []);

  const currentModel = useMemo(
    () => models.find((m) => m.id === model),
    [models, model]
  );
  const supportsImg2Img = Boolean(currentModel?.img2img);
  const squareOnly = Boolean(currentModel?.squareOnly);
  const sizes = squareOnly ? SIZES.filter((s) => s.w === 1024 && s.h === 1024) : SIZES;

  useEffect(() => {
    if (!supportsImg2Img && refImage) setRefImage(null);
  }, [supportsImg2Img, refImage]);

  useEffect(() => {
    if (squareOnly && (size.w !== 1024 || size.h !== 1024)) setSize(SIZES[0]);
  }, [squareOnly, size.w, size.h]);

  const onPickRef = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRefImage(reader.result);
    reader.readAsDataURL(file);
  };

  const generate = async (promptOverride) => {
    const nextPrompt = resolvePromptText(promptOverride, prompt);
    if (!nextPrompt || loading) return;
    setPrompt(nextPrompt);
    setLoading(true);
    setError(null);
    setStatus(t("common.starting", "Starting…"));
    try {
      const res = await generateImage(
        {
          model,
          prompt: nextPrompt,
          negative_prompt: negative,
          width: size.w,
          height: size.h,
          seed: Number(seed) || 0,
          ...(supportsImg2Img && refImage
            ? { image: refImage, strength: Number(strength) }
            : {}),
        },
        { onStatus: setStatus }
      );
      setHistory((h) => [
        {
          src: res.image,
          prompt: nextPrompt,
          model,
          size: `${size.w}×${size.h}`,
          ref: supportsImg2Img && refImage ? refImage : null,
          id: Date.now(),
        },
        ...h,
      ]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  const randomSeed = () => setSeed(Math.floor(Math.random() * 1_000_000));

  usePendingTask("/image", (task) => {
    setTaskBanner(task.rawPrompt || task.prompt);
    generate(task.prompt);
  });

  const taskDone = useHomeTaskDone(loading, error, Boolean(taskBanner));

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader
        icon={ImageIcon}
        title={t("home.feature.image.title", "AI Image Generation")}
        subtitle={t(
          "image.subtitle",
          "Generate from a text prompt with NVIDIA NIM, Together AI, and Pollinations."
        )}
      />

      <TaskBanner
        label={t("nav.image", "Image Generation")}
        prompt={taskBanner}
        running={loading}
        error={error}
        completed={taskDone}
      />

      <GenerationProgress
        active={loading}
        message={status}
        hint={t(
          "image.hint",
          "Usually takes 5–30 seconds. If rate-limited, wait ~15s and retry."
        )}
        className="mx-4 md:mx-10 mt-3"
      />

      <div className="flex-1 px-4 md:px-10 py-6 grid lg:grid-cols-[380px,1fr] gap-6 max-w-7xl mx-auto w-full">
        <aside className="space-y-4">
          <div className="card space-y-4">
            <div>
              <label className="label">{t("common.model", "Model")}</label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="input"
              >
                {models.length === 0 && <option value={model}>{model}</option>}
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">{t("common.prompt", "Prompt")}</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t(
                  "image.promptPlaceholder",
                  "Describe the image you want…"
                )}
                className="textarea min-h-[120px]"
              />
              <SpeechToTextButton
                className="mt-2"
                onTranscript={(text) =>
                  setPrompt((prev) => appendTranscript(prev, text))
                }
                onError={(msg) => setError(msg)}
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPrompt(p)}
                    className="text-[11px] text-white/60 hover:text-white border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.05] rounded-full px-2.5 py-1 transition"
                  >
                    {p.slice(0, 32)}…
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="label !mb-0">
                  {t("image.referenceImage", "Reference image")}
                </label>
                {!supportsImg2Img && (
                  <span className="text-[10px] text-white/40 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    {t("image.switchForReference", "This model is text-to-image only")}
                  </span>
                )}
              </div>
              <div className="mt-1.5">
                {refImage ? (
                  <div className="relative rounded-lg overflow-hidden border border-white/10">
                    <img
                      src={refImage}
                      alt="reference"
                      className="w-full h-auto block max-h-48 object-cover"
                    />
                    <button
                      onClick={() => setRefImage(null)}
                      className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 backdrop-blur text-white flex items-center justify-center hover:bg-black/90"
                      title={t("common.remove", "Remove")}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    className={[
                      "flex flex-col items-center justify-center gap-2 border border-dashed rounded-lg p-5 transition",
                      supportsImg2Img
                        ? "border-white/15 cursor-pointer hover:bg-white/[0.03] hover:border-white/25"
                        : "border-white/5 opacity-50 cursor-not-allowed",
                    ].join(" ")}
                  >
                    <Upload className="h-5 w-5 text-white/50" />
                    <span className="text-xs text-white/60">
                      {supportsImg2Img
                        ? t("image.uploadReference", "Click to upload a reference image")
                        : t("image.notSupported", "Not supported by current model")}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={!supportsImg2Img}
                      onChange={onPickRef}
                    />
                  </label>
                )}
              </div>
              {supportsImg2Img && refImage && (
                <div className="mt-3">
                  <label className="label flex items-center justify-between">
                    <span>{t("image.strength", "Strength")}</span>
                    <span className="text-white/60 normal-case tracking-normal font-normal">
                      {Number(strength).toFixed(2)}
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.05"
                    value={strength}
                    onChange={(e) => setStrength(e.target.value)}
                    className="w-full accent-consensus-500"
                  />
                  <div className="flex justify-between text-[10px] text-white/30 mt-1">
                    <span>{t("image.keepInput", "Keep input")}</span>
                    <span>{t("image.followPrompt", "Follow prompt")}</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="label">
                {t("image.negativePrompt", "Negative prompt")}
              </label>
              <input
                value={negative}
                onChange={(e) => setNegative(e.target.value)}
                placeholder={t(
                  "image.negativePlaceholder",
                  "What to avoid (optional)"
                )}
                className="input"
              />
              <SpeechToTextButton
                className="mt-2"
                onTranscript={(text) =>
                  setNegative((prev) => appendTranscript(prev, text))
                }
                onError={(msg) => setError(msg)}
              />
            </div>

            <div>
              <label className="label">{t("common.size", "Size")}</label>
              <div className="grid grid-cols-2 gap-1.5">
                {sizes.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => setSize(s)}
                    className={[
                      "rounded-lg px-2.5 py-2 text-xs border transition",
                      size.label === s.label
                        ? "border-consensus-500/40 bg-consensus-500/10 text-consensus-200"
                        : "border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/5",
                    ].join(" ")}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">{t("common.seed", "Seed")}</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                  className="input"
                />
                <button
                  onClick={randomSeed}
                  className="btn-secondary px-3"
                  title={t("image.randomSeed", "Random seed")}
                >
                  <Dices className="h-4 w-4" />
                </button>
              </div>
            </div>

            <button
              onClick={() => generate()}
              disabled={loading || !prompt.trim()}
              className="btn-primary w-full py-2.5"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("common.generating", "Generating…")}
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" />
                  {t("common.generate", "Generate")}
                </>
              )}
            </button>
            {error && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </aside>

        <section className="space-y-6 min-w-0">
          {loading && (
            <div className="card aspect-square max-w-xl mx-auto flex flex-col items-center justify-center gap-3 text-white/60">
              <Loader2 className="h-8 w-8 animate-spin text-consensus-400" />
              <div className="text-sm">{t("image.painting", "Painting pixels…")}</div>
            </div>
          )}
          {!loading && history.length === 0 && (
            <div className="card aspect-square max-w-xl mx-auto flex flex-col items-center justify-center text-center p-10">
              <div className="h-14 w-14 rounded-2xl bg-consensus-500/10 border border-consensus-500/30 flex items-center justify-center text-consensus-300 mb-4 shadow-glow">
                <ImageIcon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold">
                {t("image.emptyTitle", "Your gallery is empty")}
              </h3>
              <p className="text-sm text-white/50 mt-2 max-w-sm">
                {t(
                  "image.emptyBody",
                  "Describe an image on the left and hit Generate. Results appear here."
                )}
              </p>
            </div>
          )}
          {history.map((item) => (
            <div key={item.id} className="card overflow-hidden p-0">
              <img
                src={item.src}
                alt={item.prompt}
                className="w-full h-auto block"
              />
              <div className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  {item.ref && (
                    <img
                      src={item.ref}
                      alt="reference used"
                      className="h-12 w-12 rounded-md object-cover border border-white/10 shrink-0"
                      title="Reference image"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm text-white/90 line-clamp-2">
                      {item.prompt}
                    </div>
                    <div className="text-[11px] text-white/40 mt-1">
                      {item.model} · {item.size}
                      {item.ref && " · img2img"}
                    </div>
                  </div>
                </div>
                <a
                  href={item.src}
                  download={`consensus-${item.id}.png`}
                  className="btn-secondary shrink-0"
                >
                  <Download className="h-4 w-4" />
                  {t("common.save", "Save")}
                </a>
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
